/**
 * RKH (Rencana Kerja Harian) Types
 * TypeScript interfaces for RKH domain models
 */

import exp from "constants";

/**
 * Stage values for RKH
 */
export type RKHStage = 'P' | 'C' | 'F';

export const RKH_STAGE_LABELS: Record<RKHStage, string> = {
    P: 'Presence',
    C: 'Complete',
    F: 'Final'
};

export const RKH_STAGE_COLORS: Record<RKHStage, string> = {
    P: 'blue',
    C: 'orange',
    F: 'green'
};

/**
 * Harvester data in RKH
 */
export interface RKHHarvester {
    id: string;
    foreman_id: string;
    emp_id: number;
    nip: string;
    fp_id: string;
    name: string;
    job_level_id: number;
    job_level_name: string;
    job_id: number;
    job_name: string;
    is_asistensi: boolean;
    profile_id: string;
    date_sync: string | null;
    create_date: string;
    write_date: string;
    time_in: string | null;
    time_out: string | null;
    attendance_code: string | null;
}

/**
 * Foreman group in RKH
 */
export interface RKHForemanGroup {
    id: string;
    rkh_id: string;
    foreman_group_id: number;
    foreman_group_name: string;
    foreman_id: number;
    foreman_name: string;
    foreman1_id: number;
    foreman1_name: string;
    kerani_harvest_id: number;
    kerani_harvest_name: string;
    profile_id: string;
    date_sync: string | null;
    create_date: string;
    write_date: string;
    harvesters: RKHHarvester[];
}

/**
 * Location/Block data in RKH
 */
export interface RKHLocation {
    id: string;
    rkh_id: string;
    block_id: number;
    block_code: string;
    harvest_area: number;
    est_weight: number;
    est_bunch: number;
    est_hk: number;
    est_output: number;
    profile_id: string;
    date_sync: string | null;
}
/**
 * Main RKH entity (list item)
 */
export interface RKH {
    id: string;
    rkh_nbr: string;
    rkh_date: string;
    stage: RKHStage;
    company_id: number;
    estate_id: number;
    estate_name: string;
    division_id: number;
    division_name: string;
    profile_id: string;
    date_sync: string | null;
    create_date: string;
    write_date: string;
}

/**
 * RKH with full details including foreman groups and locations
 */
export interface RKHDetail extends RKH {
    foreman_groups: RKHForemanGroup[];
    locations: RKHLocation[];
}

/**
 * Pagination metadata
 */
export interface RKHPagination {
    page: number;
    limit: number;
    total_items: number;
    total_pages: number;
}

/**
 * API response for RKH list
 */
export interface RKHListResponse {
    success: boolean;
    data: RKH[];
    pagination: RKHPagination;
}

/**
 * API response for RKH detail
 */
export interface RKHDetailResponse {
    success: boolean;
    data: RKHDetail;
}

/**
 * Filter parameters for RKH list
 */
export interface RKHFilters {
    date_from?: string;
    date_to?: string;
    estate_id?: number | null;
    division_id?: number | null;
    stage?: RKHStage | null;
    page?: number;
    limit?: number;
}

/**
 * Statistics summary for RKH
 */
export interface RKHStats {
    total_rkh: number;
    by_stage: {
        P: number;
        C: number;
        F: number;
    };
    total_harvesters: number;
    total_blocks: number;
    total_area: number;
    total_est_weight: number;
}

export interface RKHTotal {
    company_id: number;
    harvest_area: number;
    harvest_area_target: number;
    est_weight: number;
    est_bunch: number;
    est_hk: number;
    est_output: number;
}


export interface RKHBlock {
    block_id: number;
    block_code: string;
    harvest_area: number;
    is_carry_over: boolean;
    harvest_area_target: number;
    total_ancak?: number;
    ancaks?: string[];
    akp?: number;
    est_weight: number;
    est_bunch: number;
    est_hk: number;
    est_output: number;
}
export interface UnplannedBlocks {
    block_id: number;
    block_code: string;
}
export interface RKHDivisionGroup {
    division_id: number;
    division: string;
    rkh_id?: string;
    rkh_date: string;
    blocks: RKHBlock[];
    unplanned_blocks: UnplannedBlocks[];
    division_total: RKHTotal;
}

export interface RKHEstateGroup {
    estate_id: number;
    estate: string;
    divisions: RKHDivisionGroup[];
    estate_total: RKHTotal;
}

export interface RKHCompanyGroup {
    company_id: number;
    company: string | null;
    estates: RKHEstateGroup[];
    company_total: RKHTotal;
}

/**
 * API response for RKH summary
 */
export interface RKHSummaryResponse {
    success: boolean;
    data: RKHCompanyGroup[];
}

/**
 * Filter parameters for RKH summary
 */
export interface RKHSummaryFilters {
    date_from?: string;
    date_to?: string;
    company_id?: number | null;
    estate_id?: number | null;
    division_id?: number | null;
}

/**
 * Taksasi data for a specific date
 */
export interface TaksasiData {
    exists: boolean;
    date?: string | null;
    est_weight: number;
    est_ripe_bunch: number;
    est_hk: number;
    est_output: number;
}

/**
 * Available block for location selection
 */
export interface AvailableBlock {
    block_id: number;
    block_code: string;
    planted_area: number;
    /** Whether this block is already registered as a location for this RKH */
    is_selected: boolean;
    /** Taksasi estimate on the exact RKH target date */
    target_date_taksasi: TaksasiData;
    /** Most recent taksasi available in the database (used for Carry Over) */
    last_taksasi: TaksasiData;
}

/**
 * API response for available blocks
 */
export interface AvailableBlocksResponse {
    success: boolean;
    rkh: RKH;
    data: AvailableBlock[];
}

/**
 * Location input for registration
 */
export interface LocationInput {
    block_id: number;
    block_code: string;
    is_carry_over: boolean;
    harvest_area_target: number;
    hanca_cnt: number;
}

/**
 * Request body for location registration
 */
export interface LocationRegistrationRequest {
    create_by: string;
    write_by: string;
    locations: LocationInput[];
}

/**
 * API response for location registration
 */
export interface LocationRegistrationResponse {
    success: boolean;
    message: string;
    error?: string;
}
